import { challengeNumberToMapId, currentChallengeNumber } from "@dailywars/core/engine";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { useEndTurnConfirmation } from "#/components/game/useEndTurnConfirmation";
import { PlayMapAccessGate } from "#/components/play/PlayMapAccessUpsell";
import { PlayMapPageShell } from "#/components/play/PlayMapPageShell";
import { playSessionConfig } from "#/components/play/playSession";
import {
  useAiPlaybackStepTimer,
  useEscapeCancelsSelection,
  usePlayMapBoardChrome,
  usePlayMapGameHotkeys,
  usePlayMapTurnActions,
  usePlayMapUndo,
  useVictoryAutoSubmitTurn,
} from "#/components/play/usePlayMapSession";
import { BoardImagesGate } from "#/game/BoardImagesGate";
import { GameStoreProvider, useGameSelector } from "#/game/GameStoreProvider";
import { readAnonymousPlayerId, syncAnonymousPlayerIdOnClient } from "#/lib/anonymousPlayerId";
import {
  challengeMapDescription,
  challengeMapTitle,
  documentHead,
  mapOgImageUrl,
} from "#/lib/documentTitle";
import { gameSettingsForSession } from "#/lib/guestGameplaySettings";
import { isMapAccessUpsellReason } from "#/lib/mapAccessUpsellCopy";
import { isRegisteredPlayerId } from "#/lib/playerDisplayName";
import { useRequestGameContext } from "#/lib/useRequestGameContext";
import { loadChallengeMapPage, type PlayMapPageReady } from "#/server/game/playMapPage";

function readPlayerId(): string | undefined {
  return readAnonymousPlayerId();
}

export const Route = createFileRoute("/challenge")({
  loader: async () => {
    const challengeNumber = currentChallengeNumber();
    const mapId = challengeNumberToMapId(challengeNumber);
    const playerId = readPlayerId();
    const result = await loadChallengeMapPage({
      data: playerId ? { mapId, playerId } : { mapId },
    });
    if (result.status === "access-denied" && result.reason === "invalid-id") {
      throw notFound();
    }
    if (result.status === "access-denied" && result.reason === "challenge-attempt-used") {
      throw redirect({
        to: "/challenge/$challengeNumber/leaderboard",
        params: { challengeNumber: String(challengeNumber) },
      });
    }
    if (playerId && result.status !== "access-denied") {
      syncAnonymousPlayerIdOnClient({
        session: {
          playerId: result.playerId,
          isAnonymous: !isRegisteredPlayerId(result.playerId),
        },
      });
    }
    return result;
  },
  head: () => {
    const challengeNumber = currentChallengeNumber();
    const mapId = challengeNumberToMapId(challengeNumber);
    return documentHead(challengeMapTitle(challengeNumber), {
      description: challengeMapDescription(challengeNumber),
      canonicalPath: "/challenge",
      imageUrl: mapOgImageUrl(mapId),
    });
  },
  component: ChallengePlayPage,
});

function ChallengePlayPage() {
  const loaderData = Route.useLoaderData();

  if (loaderData.status === "access-denied") {
    if (!isMapAccessUpsellReason(loaderData.reason)) {
      throw notFound();
    }
    return <PlayMapAccessGate reason={loaderData.reason} mapId={loaderData.mapId} />;
  }

  return <ChallengePlayReadyPage initial={loaderData} />;
}

function ChallengePlayReadyPage({ initial }: { initial: PlayMapPageReady }) {
  const session = useRequestGameContext();
  return (
    <BoardImagesGate state={initial.state} mapId={initial.mapId}>
      <GameStoreProvider
        initialState={initial.state}
        initialServerDayNumber={initial.currentDay}
        settings={gameSettingsForSession(session)}
      >
        <ChallengePlaySession initial={initial} />
      </GameStoreProvider>
    </BoardImagesGate>
  );
}

function useChallengePlayControls(initial: PlayMapPageReady) {
  useAiPlaybackStepTimer();
  useEscapeCancelsSelection();
  useVictoryAutoSubmitTurn({ playerId: initial.playerId, mapId: initial.mapId });
  const endTurnConfirmation = useGameSelector((s) => s.endTurnConfirmation);
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const playSession = playSessionConfig({ kind: "challenge", mapId: initial.mapId });
  const { handleEndTurn, handleRestart, handleUndoCommittedTurn, isRestarting } =
    usePlayMapTurnActions({
      playerId: initial.playerId,
      mapId: initial.mapId,
      routeMapId: initial.mapId,
    });
  const endTurn = useEndTurnConfirmation({
    onConfirm: handleEndTurn,
    pending: isSubmitting,
    confirmBeforeEndTurn: endTurnConfirmation,
  });
  const undo = usePlayMapUndo({
    onUndoCommittedTurn: handleUndoCommittedTurn,
    pending: isSubmitting,
    allowUndoFullTurn: playSession.allowUndoFullTurn,
  });
  return { handleRestart, isRestarting, endTurn, undo, playSession };
}

function ChallengePlaySession({ initial }: { initial: PlayMapPageReady }) {
  const { handleRestart, isRestarting, endTurn, undo, playSession } =
    useChallengePlayControls(initial);
  const boardDialogOpen = endTurn.dialogOpen || undo.undoDialogOpen;

  usePlayMapGameHotkeys({
    onEndTurn: endTurn.requestEndTurn,
    onUndo: undo.handleUndo,
    paused: boardDialogOpen,
    allowUndoFullTurn: playSession.allowUndoFullTurn,
  });

  const chrome = usePlayMapBoardChrome();

  return (
    <>
      <PlayMapPageShell
        handleUndo={undo.handleUndo}
        handleEndTurn={endTurn.requestEndTurn}
        handleRestart={handleRestart}
        isRestarting={isRestarting}
        boardDialogOpen={boardDialogOpen}
        medalThresholds={null}
        session={playSession}
        {...chrome}
      />
      {endTurn.confirmDialog}
    </>
  );
}
